import { privateApi } from '../config/api';

class P2PService {
  // Payment Methods
  async getPaymentMethods() {
    try {
      const response = await privateApi.get('/p2p/payment-methods/');
      return response.data;
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      throw error;
    }
  }

  // User Profile
  async getProfile() {
    try {
      const response = await privateApi.get('/p2p/profile/');
      return response.data;
    } catch (error) {
      console.error('Error fetching P2P profile:', error);
      throw error;
    }
  }

  async createProfile(profileData) {
    try {
      const response = await privateApi.post('/p2p/profile/create/', profileData);
      return response.data;
    } catch (error) {
      console.error('Error creating P2P profile:', error);
      throw error;
    }
  }

  async updateProfile(profileData) {
    try {
      const response = await privateApi.patch('/p2p/profile/', profileData);
      return response.data;
    } catch (error) {
      console.error('Error updating P2P profile:', error);
      throw error;
    }
  }

  // Advertisements
  async getAds(filters = {}) {
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });
      
      const response = await privateApi.get(`/p2p/ads/?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching ads:', error);
      throw error;
    }
  }

  async getMyAds(params = {}) {
    try {
      const response = await privateApi.get('/p2p/ads/my/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching my ads:', error);
      throw error;
    }
  }

  async createAd(adData) {
    try {
      const response = await privateApi.post('/p2p/ads/my/', adData);
      return response.data;
    } catch (error) {
      console.error('Error creating ad:', error);
      throw error;
    }
  }

  async getAdDetail(adId) {
    try {
      const response = await privateApi.get(`/p2p/ads/${adId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching ad detail:', error);
      throw error;
    }
  }

  async updateAd(adId, adData) {
    try {
      const response = await privateApi.patch(`/p2p/ads/${adId}/`, adData);
      return response.data;
    } catch (error) {
      console.error('Error updating ad:', error);
      throw error;
    }
  }

  async deleteAd(adId) {
    try {
      const response = await privateApi.delete(`/p2p/ads/${adId}/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting ad:', error);
      throw error;
    }
  }

  // Orders
  async getOrders(filters = {}) {
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });
      
      const response = await privateApi.get(`/p2p/orders/?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  }

  async createOrder(orderData) {
    try {
      const response = await privateApi.post('/p2p/orders/create/', orderData);
      return response.data;
    } catch (error) {
      console.error('Error creating order:', error);
      // Extract detailed error message from backend response
      const errorMessage = this.extractErrorMessage(error);
      const enhancedError = new Error(errorMessage);
      enhancedError.response = error.response;
      enhancedError.originalError = error;
      throw enhancedError;
    }
  }

  // Helper method to extract error messages from API responses
  extractErrorMessage(error) {
    if (error.response?.data) {
      const data = error.response.data;
      
      // Check for non_field_errors (common Django REST Framework format)
      if (data.non_field_errors && Array.isArray(data.non_field_errors)) {
        if (data.non_field_errors[0]?.message) {
          return data.non_field_errors[0].message;
        }
        if (typeof data.non_field_errors[0] === 'string') {
          return data.non_field_errors[0];
        }
      }
      
      // Check for field-specific errors
      if (data.detail) {
        return data.detail;
      }
      
      // Check for individual field errors
      const fieldErrors = Object.keys(data)
        .filter(key => key !== 'non_field_errors')
        .map(key => {
          const fieldError = data[key];
          if (Array.isArray(fieldError)) {
            if (fieldError[0]?.message) {
              return `${key}: ${fieldError[0].message}`;
            }
            return `${key}: ${fieldError[0]}`;
          }
          if (typeof fieldError === 'object' && fieldError.message) {
            return `${key}: ${fieldError.message}`;
          }
          return `${key}: ${fieldError}`;
        });
      
      if (fieldErrors.length > 0) {
        return fieldErrors.join(', ');
      }
      
      // If data is a string
      if (typeof data === 'string') {
        return data;
      }
    }
    
    // Fallback to error message or generic message
    return error.message || 'An error occurred while processing your request';
  }

  async getOrderDetail(orderId) {
    try {
      const response = await privateApi.get(`/p2p/orders/${orderId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching order detail:', error);
      throw error;
    }
  }

  // Order Actions
  async markOrderPaid(orderId, paymentReference = '') {
    try {
      const response = await privateApi.post(`/p2p/orders/${orderId}/mark-paid/`, {
        payment_reference: paymentReference
      });
      return response.data;
    } catch (error) {
      console.error('Error marking order as paid:', error);
      const errorMessage = this.extractErrorMessage(error);
      const enhancedError = new Error(errorMessage);
      enhancedError.response = error.response;
      enhancedError.originalError = error;
      throw enhancedError;
    }
  }

  async confirmPayment(orderId) {
    try {
      const response = await privateApi.post(`/p2p/orders/${orderId}/confirm-payment/`);
      return response.data;
    } catch (error) {
      console.error('Error confirming payment:', error);
      const errorMessage = this.extractErrorMessage(error);
      const enhancedError = new Error(errorMessage);
      enhancedError.response = error.response;
      enhancedError.originalError = error;
      throw enhancedError;
    }
  }

  async releaseCrypto(orderId) {
    try {
      const response = await privateApi.post(`/p2p/orders/${orderId}/release/`);
      return response.data;
    } catch (error) {
      console.error('Error releasing crypto:', error);
      const errorMessage = this.extractErrorMessage(error);
      const enhancedError = new Error(errorMessage);
      enhancedError.response = error.response;
      enhancedError.originalError = error;
      throw enhancedError;
    }
  }

  async cancelOrder(orderId, reason = '') {
    try {
      const response = await privateApi.post(`/p2p/orders/${orderId}/cancel/`, {
        reason: reason
      });
      return response.data;
    } catch (error) {
      console.error('Error cancelling order:', error);
      const errorMessage = this.extractErrorMessage(error);
      const enhancedError = new Error(errorMessage);
      enhancedError.response = error.response;
      enhancedError.originalError = error;
      throw enhancedError;
    }
  }

  // Chat
  async getChatMessages(orderId) {
    try {
      const response = await privateApi.get(`/p2p/orders/${orderId}/chat/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching chat messages:', error);
      throw error;
    }
  }

  async sendChatMessage(orderId, messageData) {
    try {
      const response = await privateApi.post(`/p2p/orders/${orderId}/chat/`, messageData);
      return response.data;
    } catch (error) {
      console.error('Error sending chat message:', error);
      throw error;
    }
  }

  // Disputes
  async createDispute(orderId, disputeData) {
    try {
      const response = await privateApi.post(`/p2p/orders/${orderId}/dispute/`, disputeData);
      return response.data;
    } catch (error) {
      console.error('Error creating dispute:', error);
      throw error;
    }
  }

  // Feedback
  async createFeedback(orderId, feedbackData) {
    try {
      const response = await privateApi.post(`/p2p/orders/${orderId}/feedback/`, feedbackData);
      return response.data;
    } catch (error) {
      console.error('Error creating feedback:', error);
      throw error;
    }
  }

  // Statistics
  async getStats() {
    try {
      const response = await privateApi.get('/p2p/stats/');
      return response.data;
    } catch (error) {
      console.error('Error fetching P2P stats:', error);
      throw error;
    }
  }

  // Settings
  async getSettings() {
    try {
      const response = await privateApi.get('/p2p/settings/');
      return response.data;
    } catch (error) {
      console.error('Error fetching P2P settings:', error);
      throw error;
    }
  }

  // WebSocket helpers
  connectOrderWebSocket(orderId, callbacks = {}) {
    const wsUrl = `${process.env.REACT_APP_WS_URL || 'ws://localhost:8000'}/ws/p2p/order/${orderId}/`;
    const socket = new WebSocket(wsUrl);

    socket.onopen = () => {
      console.log('Connected to P2P order WebSocket');
      if (callbacks.onOpen) callbacks.onOpen();
    };

    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (callbacks.onMessage) callbacks.onMessage(data);
    };

    socket.onclose = () => {
      console.log('Disconnected from P2P order WebSocket');
      if (callbacks.onClose) callbacks.onClose();
    };

    socket.onerror = (error) => {
      console.error('P2P order WebSocket error:', error);
      if (callbacks.onError) callbacks.onError(error);
    };

    return socket;
  }

  connectMarketWebSocket(callbacks = {}) {
    const wsUrl = `${process.env.REACT_APP_WS_URL || 'ws://localhost:8000'}/ws/p2p/market/`;
    const socket = new WebSocket(wsUrl);

    socket.onopen = () => {
      console.log('Connected to P2P market WebSocket');
      if (callbacks.onOpen) callbacks.onOpen();
    };

    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (callbacks.onMessage) callbacks.onMessage(data);
    };

    socket.onclose = () => {
      console.log('Disconnected from P2P market WebSocket');
      if (callbacks.onClose) callbacks.onClose();
    };

    socket.onerror = (error) => {
      console.error('P2P market WebSocket error:', error);
      if (callbacks.onError) callbacks.onError(error);
    };

    return socket;
  }

  connectUserWebSocket(callbacks = {}) {
    const wsUrl = `${process.env.REACT_APP_WS_URL || 'ws://localhost:8000'}/ws/p2p/user/`;
    const socket = new WebSocket(wsUrl);

    socket.onopen = () => {
      console.log('Connected to P2P user WebSocket');
      if (callbacks.onOpen) callbacks.onOpen();
    };

    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (callbacks.onMessage) callbacks.onMessage(data);
    };

    socket.onclose = () => {
      console.log('Disconnected from P2P user WebSocket');
      if (callbacks.onClose) callbacks.onClose();
    };

    socket.onerror = (error) => {
      console.error('P2P user WebSocket error:', error);
      if (callbacks.onError) callbacks.onError(error);
    };

    return socket;
  }
}

export default new P2PService();
